import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { WIDGET_DESIGN_AGENT_ID, copilotPanel } from '@inference.engine/agentic-ui/components';
import { useCopilotRequests, type FocusedWidget } from './copilot-requests';

export type BuilderView = 'build' | 'widgets' | 'test';

/** The builder page's query parameter that opens it on Widgets with that widget. */
// eslint-disable-next-line i18next/no-literal-string -- a URL parameter, not copy
export const WIDGET_PARAM = 'widget';

export interface BuilderViews {
  view: BuilderView;
  /** From the header: Widgets opens with its list, so the person can pick one. */
  setView: (view: BuilderView) => void;
  widget: FocusedWidget | null;
  listOpen: boolean;
  setListOpen: (open: boolean) => void;
  /** Shows a widget, and names it in Flow.Assist's turns so the Widget agent works on it. */
  openWidget: (widget: FocusedWidget, options: { list: boolean }) => void;
  /** Flow.Assist's chat changed hands: the Widget agent's turn shows the widget it builds. */
  onAgentChange: (agentId: string) => void;
  /** A widget was deleted: it no longer shows, nor is it named in Flow.Assist's turns. */
  forgetWidget: (widgetId: string) => void;
  /** The open widget's name, once known for one opened by id alone. */
  nameWidget: (widget: FocusedWidget) => void;
}

/**
 * Which of the builder's views shows, and the Widgets view's open widget and list. A hand-off to the
 * Widget agent moves to Widgets; its hand-back leaves the page where it is.
 */
export function useBuilderViews(initialWidgetId?: string | null): BuilderViews {
  const [view, setViewState] = useState<BuilderView>(initialWidgetId ? 'widgets' : 'build');
  const [widget, setWidget] = useState<FocusedWidget | null>(
    initialWidgetId ? { id: initialWidgetId, name: '' } : null,
  );
  const [listOpen, setListOpen] = useState(false);
  const widgetRef = useRef(widget);
  widgetRef.current = widget;

  useEffect(() => {
    if (!initialWidgetId) return;
    useCopilotRequests.setState({ focusedWidget: { id: initialWidgetId, name: '' } });
    copilotPanel.open();
    // The page's address names the widget once, as it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setView = useCallback((next: BuilderView) => {
    setViewState(next);
    if (next !== 'widgets') return;
    setListOpen(true);
    // A widget changes only through the conversation, so Widgets always has it beside it.
    copilotPanel.open();
  }, []);

  const openWidget = useCallback((next: FocusedWidget, { list }: { list: boolean }) => {
    setWidget(next);
    setListOpen(list);
    useCopilotRequests.setState({ focusedWidget: next });
  }, []);

  const onAgentChange = useCallback((agentId: string) => {
    if (agentId !== WIDGET_DESIGN_AGENT_ID) return;
    setViewState('widgets');
    // A conversation reopened mid-hand-off names no widget yet: offer the list rather than nothing.
    setListOpen(widgetRef.current === null);
  }, []);

  const forgetWidget = useCallback((widgetId: string) => {
    if (widgetRef.current?.id === widgetId) {
      setWidget(null);
      setListOpen(true);
    }
    if (useCopilotRequests.getState().focusedWidget?.id === widgetId) {
      useCopilotRequests.setState({ focusedWidget: null });
    }
  }, []);

  const nameWidget = useCallback((named: FocusedWidget) => {
    if (widgetRef.current?.id !== named.id || widgetRef.current.name === named.name) return;
    setWidget(named);
    if (useCopilotRequests.getState().focusedWidget?.id === named.id) {
      useCopilotRequests.setState({ focusedWidget: named });
    }
  }, []);

  return {
    view,
    setView,
    widget,
    listOpen,
    setListOpen,
    openWidget,
    onAgentChange,
    forgetWidget,
    nameWidget,
  };
}

/**
 * Drops `?widget=` once the page leaves Widgets: it only says where the page opens, and kept, a
 * reload would bring the person back to a view they had left.
 */
export function useDropWidgetParam(view: BuilderView): void {
  const [search, setSearch] = useSearchParams();
  const named = search.has(WIDGET_PARAM);
  useEffect(() => {
    if (view === 'widgets' || !named) return;
    setSearch(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(WIDGET_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [view, named, setSearch]);
}
